import { heatFrpProxy } from "./heat";
import { resolveEventName } from "./event-names";
import { countryName } from "./geo";
import { snapshotQuery } from "./imagery";
import { frpEventSeries, type FrpPoint } from "./series";
import { CLAIM } from "./sources";
import { isImportantEvent } from "./status";
import type { Detection, EventStatus, FiresResponse, ImageryThumb, Incident, IncidentDetail } from "./types";

/** One saved major event — sidebar facts plus a collapsed FRP series. */
export type EventCard = {
  id: string;
  place_name: string | null;
  country: string;
  lat: number;
  lon: number;
  first_seen: string;
  last_seen: string;
  last_observed: string;
  last_retrieved: string | null;
  frp_mw: number;
  peak_frp_mw: number;
  detection_count: number;
  event_status: EventStatus | null;
  fire_probability: number | null;
  last_important_at: string | null;
  series: FrpPoint[];
  live: boolean;
  /** Saved pixels for this one fire. List API omits this — only /api/incidents loads it. */
  heat?: EventHeatPoint[];
};

/** Compact D1 tuple: lon, lat, FRP, ISO time, platform. */
export type EventHeatTuple = [number, number, number, string, string?];

export type EventHeatPoint = {
  lon: number;
  lat: number;
  frp: number;
  observed_at: string;
  source: string;
};

/** ~80 B/point. A few hundred majors/year stays well under a D1 megabyte class. */
export const EVENT_HEAT_MAX = 4000;
/** Always keep the hottest cells when we downsample a mega-scar. */
const EVENT_HEAT_HOTTEST = 80;

/** Keep a long MTG campaign small enough for a card / D1 row. Always keep the peak. */
export function capSeries(points: FrpPoint[], max = 160): FrpPoint[] {
  if (points.length <= max) return points;
  const peak = points.reduce((best, p) => (p.mw > best.mw ? p : best), points[0]!);
  const out: FrpPoint[] = [];
  const seen = new Set<number>();
  const step = (points.length - 1) / (max - 1);
  for (let i = 0; i < max; i++) {
    const p = points[Math.round(i * step)]!;
    if (seen.has(p.t)) continue;
    seen.add(p.t);
    out.push(p);
  }
  if (!seen.has(peak.t)) out.push(peak);
  return out.sort((a, b) => a.t - b.t);
}

export function parseSeriesJson(raw: string | null | undefined): FrpPoint[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as FrpPoint[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p) => Number.isFinite(p?.t) && Number.isFinite(p?.mw));
  } catch {
    return [];
  }
}

/** True when pixels are not all stacked on one centroid (archive fallback). */
export function detectionsHaveSpread(dets: { lat: number; lon: number }[]): boolean {
  if (dets.length < 2) return false;
  const lon0 = dets[0]!.lon;
  const lat0 = dets[0]!.lat;
  // ~100 m — VIIRS cells and a real scar clear this; a fake centroid stack does not.
  return dets.some((d) => Math.abs(d.lon - lon0) > 0.001 || Math.abs(d.lat - lat0) > 0.001);
}

function heatKey(p: EventHeatPoint): string {
  return `${p.observed_at}:${p.lon.toFixed(4)}:${p.lat.toFixed(4)}`;
}

export function parseEventHeat(raw: string | null | undefined): EventHeatPoint[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: EventHeatPoint[] = [];
    for (const item of parsed) {
      if (Array.isArray(item)) {
        const lon = Number(item[0]);
        const lat = Number(item[1]);
        const frp = Number(item[2]);
        const observed_at = typeof item[3] === "string" ? item[3] : "";
        const source = typeof item[4] === "string" && item[4] ? item[4] : "CATALOG";
        if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(frp) || !observed_at) continue;
        out.push({ lon, lat, frp, observed_at, source });
        continue;
      }
      if (!item || typeof item !== "object") continue;
      const o = item as Record<string, unknown>;
      const lon = Number(o.lon);
      const lat = Number(o.lat);
      const frp = Number(o.frp ?? o.frp_mw);
      const observed_at = typeof o.observed_at === "string" ? o.observed_at : "";
      const source = typeof o.source === "string" && o.source ? o.source : "CATALOG";
      if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(frp) || !observed_at) continue;
      out.push({ lon, lat, frp, observed_at, source });
    }
    return out;
  } catch {
    return [];
  }
}

export function serializeEventHeat(points: EventHeatPoint[]): string {
  return JSON.stringify(points.map((p) => [p.lon, p.lat, p.frp, p.observed_at, p.source]));
}

/** Temporal sample + hottest cells. Dedupes the same look/cell. */
export function capEventHeat(points: EventHeatPoint[], max = EVENT_HEAT_MAX): EventHeatPoint[] {
  const deduped: EventHeatPoint[] = [];
  const seen = new Set<string>();
  for (const p of points) {
    const k = heatKey(p);
    if (seen.has(k)) continue;
    seen.add(k);
    deduped.push(p);
  }
  if (deduped.length <= max) {
    return deduped.sort((a, b) => a.observed_at.localeCompare(b.observed_at));
  }
  const hottest = [...deduped].sort((a, b) => b.frp - a.frp).slice(0, EVENT_HEAT_HOTTEST);
  const sorted = [...deduped].sort((a, b) => a.observed_at.localeCompare(b.observed_at));
  const step = (sorted.length - 1) / (max - 1);
  const keep = new Set<string>();
  const out: EventHeatPoint[] = [];
  const add = (p: EventHeatPoint) => {
    const k = heatKey(p);
    if (keep.has(k)) return;
    keep.add(k);
    out.push(p);
  };
  for (let i = 0; i < max; i++) add(sorted[Math.round(i * step)]!);
  for (const p of hottest) add(p);
  return out.sort((a, b) => a.observed_at.localeCompare(b.observed_at));
}

/** Union of previous snapshot + new live pixels. Empty next must not wipe the tape. */
export function mergeEventHeat(prev: EventHeatPoint[], next: EventHeatPoint[]): EventHeatPoint[] {
  if (next.length === 0) return capEventHeat(prev);
  return capEventHeat([...prev, ...next]);
}

function detectionsFromHeat(card: EventCard, heat: EventHeatPoint[]): Detection[] {
  return heat.map((p, i) => ({
    id: `${card.id}_h${i}`,
    lat: p.lat,
    lon: p.lon,
    observed_at: p.observed_at,
    source: p.source || "CATALOG",
    satellite: "",
    frp_mw: p.frp,
    confidence: 0,
    confidence_raw: "",
    daynight: "",
    incident_id: card.id,
  }));
}

function detectionsFromSeries(card: EventCard): Detection[] {
  return card.series.map((p, i) => ({
    id: `${card.id}_p${i}`,
    lat: card.lat,
    lon: card.lon,
    observed_at: new Date(p.t).toISOString(),
    source: "CATALOG",
    satellite: "",
    frp_mw: p.mw,
    confidence: 0,
    confidence_raw: "",
    daynight: "",
    incident_id: card.id,
  }));
}

/** Rebuild a panel payload from a catalog card when the live incident is gone. */
export function detailFromCatalog(card: EventCard): IncidentDetail {
  const incident: Incident = {
    id: card.id,
    label: resolveEventName(card.id, card.place_name) || card.country,
    country: card.country,
    lat: card.lat,
    lon: card.lon,
    first_seen: card.first_seen,
    last_seen: card.last_seen,
    frp_mw: card.frp_mw,
    frp_prev_mw: null,
    frp_delta_mw: 0,
    frp_delta_pct: null,
    detection_count: card.detection_count,
    status: "cooling",
    sources: [],
    last_important_at: card.last_important_at,
    fire_probability: card.fire_probability,
    event_status: card.event_status,
  };
  const heat = card.heat ?? [];
  const detections = heat.length ? detectionsFromHeat(card, heat) : detectionsFromSeries(card);
  return {
    incident,
    detections,
    last_observed: card.last_observed,
    last_retrieved: card.last_retrieved,
    ingest_lag_ms: null,
    freshness: [],
    // One Worldview frame at the last campaign look — not a live heat tape.
    imagery: catalogSnapshot(card.lat, card.lon, card.last_important_at || card.last_seen),
    wind: null,
    place_name: resolveEventName(card.id, card.place_name),
    cameras: [],
    archive: true,
  };
}

/** Daily GIBS composite at the saved point. Night if the stamp is in polar night hours. */
function catalogSnapshot(lat: number, lon: number, iso: string): ImageryThumb[] {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return [];
  const hour = new Date(t).getUTCHours();
  const night = hour >= 20 || hour < 5;
  const q = snapshotQuery("VIIRS_NOAA21", iso, lat, lon, night);
  return [
    {
      source: "VIIRS_NOAA21",
      observed_at: iso,
      lat,
      lon,
      night,
      thumb_url: `/api/imagery?${q.toString()}&size=thumb`,
      full_url: `/api/imagery?${q.toString()}&size=full`,
    },
  ];
}

export function seriesFromDetections(detections: Detection[]): FrpPoint[] {
  return capSeries(frpEventSeries(detections));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Visitor-local year-month of last activity — Events grouping key (`2026-08`). */
export function eventMonthKey(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "unknown";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function eventMonthLabel(key: string): string {
  const [year, month] = key.split("-");
  const i = Number(month) - 1;
  if (key === "unknown" || !year || !Number.isFinite(i) || !MONTHS[i]) return "Unknown";
  return `${MONTHS[i]} ${year}`;
}

/** Visitor-local month the Events dropdown should open on. */
export function currentEventMonth(nowMs = Date.now()): string {
  return eventMonthKey(new Date(nowMs).toISOString());
}

export function filterEventCards(
  cards: EventCard[],
  opts: { country?: string; month?: string; q?: string },
): EventCard[] {
  const q = (opts.q ?? "").trim().toLowerCase();
  return cards.filter((c) => {
    if (opts.country && c.country !== opts.country) return false;
    if (opts.month && opts.month !== "all" && eventMonthKey(c.last_seen) !== opts.month) return false;
    if (q) {
      const name = (resolveEventName(c.id, c.place_name) || c.place_name || "").toLowerCase();
      const country = countryName(c.country || "").toLowerCase();
      const cc = (c.country || "").toLowerCase();
      if (!name.includes(q) && !country.includes(q) && !cc.includes(q)) return false;
    }
    return true;
  });
}

/** One-fire /api/fires stand-in so the theater reuses Play, the slider, and the heatmap. */
export function firesFromDetail(detail: IncidentDetail): FiresResponse {
  const dets = detail.detections;
  let t0 = Date.parse(detail.incident.first_seen);
  let t1 = Date.parse(detail.incident.last_seen);
  const heat: FiresResponse["heat"] = [];
  for (const d of dets) {
    const t = Date.parse(d.observed_at);
    if (!Number.isFinite(t)) continue;
    if (!Number.isFinite(t0) || t < t0) t0 = t;
    if (!Number.isFinite(t1) || t > t1) t1 = t;
    // S3 has no MW — the 3rd slot is heatmap weight, not a measured intensity.
    heat.push([d.lon, d.lat, heatFrpProxy(d.source, d.frp_mw), d.observed_at, d.incident_id]);
  }
  if (!Number.isFinite(t0)) t0 = Date.now() - 3600_000;
  if (!Number.isFinite(t1) || t1 < t0) t1 = t0 + 3600_000;
  // Same 1 h floor as the sidebar replay — a ten-minute look still scrubs.
  if (t1 - t0 < 3600_000) t0 = t1 - 3600_000;
  return {
    updated_at: detail.last_observed || detail.incident.last_seen,
    claim: CLAIM,
    window: { since: new Date(t0).toISOString(), until: new Date(t1).toISOString() },
    stats: { fires: 1, new_1h: 0, rapidly_intensifying: 0, important: 1, belgium: 0 },
    detections: [],
    heat,
    incidents: [detail.incident],
  };
}

export function groupEventsByMonth(cards: EventCard[]): Array<{ key: string; label: string; cards: EventCard[] }> {
  const by = new Map<string, EventCard[]>();
  for (const c of cards) {
    const key = eventMonthKey(c.last_seen);
    const list = by.get(key) ?? [];
    list.push(c);
    by.set(key, list);
  }
  return [...by.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, group]) => ({ key, label: eventMonthLabel(key), cards: group }));
}

/** Map payload → cards when /api/events is slow or blocked. No FRP series. */
export function cardsFromIncidents(incidents: Incident[], nowMs: number): EventCard[] {
  return incidents
    .filter((i) => isImportantEvent(i, nowMs))
    .map((i) => ({
      id: i.id,
      place_name: resolveEventName(i.id, null),
      country: i.country,
      lat: i.lat,
      lon: i.lon,
      first_seen: i.first_seen,
      last_seen: i.last_seen,
      last_observed: i.last_seen,
      last_retrieved: null,
      frp_mw: i.frp_mw,
      peak_frp_mw: i.frp_mw,
      detection_count: i.detection_count,
      event_status: i.event_status ?? null,
      fire_probability: i.fire_probability ?? null,
      last_important_at: i.last_important_at ?? null,
      series: [],
      live: true,
    }))
    .sort((a, b) => b.last_seen.localeCompare(a.last_seen));
}
